// Message domain types for the frontend.
//
// The canonical contract (enums) lives in `@atlas/types`. This module re-exports
// the enums and defines the FRONTEND VIEW shape, aligned with the message-service
// DTO (Date → ISO string).

import { MessageStatus, MessageTranslationStatus, MessageType } from '@atlas/types'

export { MessageStatus, MessageTranslationStatus, MessageType }

/** A message as returned by the API (dates are ISO strings). */
export interface Message {
	messageId: string
	tenantId: number
	title: string
	description?: string
	content: string
	status: MessageStatus
	languageIso: string
	messageType: MessageType
	/** Placement within the piece — card index, video timestamp, or 'single'. */
	position?: string | null
	translationGroupId: string
	isSource: boolean
	/** State of an in-flight AI translation job for this row; null if never machine-translated. */
	translationStatus?: MessageTranslationStatus | null
	/** Preserved for display when `translationStatus` is `failed`. */
	translationFailureReason?: string | null
	isActive: boolean
	createdBy?: string | null
	createdAt: string
	updatedAt: string
	deletedAt?: string | null
}

export interface CreateMessageInput {
	title: string
	description?: string
	content: string
	status?: MessageStatus
	languageIso: string
	messageType: MessageType
	position?: string
	translationGroupId?: string
	isSource?: boolean
}

export type UpdateMessageInput = Partial<CreateMessageInput>

export interface MessageListParams {
	search?: string
	status?: MessageStatus
	languageIso?: string
	messageType?: MessageType
	translationGroupId?: string
	/** Pagination, sent as top-level query params rather than inside the JSON `filter`. */
	page?: number
	limit?: number
}

/** The largest page the backend serves (`MAX_LIMIT` in `message-controller.ts`); a larger `limit` is clamped down server-side. */
export const MESSAGE_LIST_MAX_LIMIT = 100

/** Mirrors `messageRequestTranslationSchema`'s `targetLocales` cap (`message.schema.ts`) so an over-long list is rejected client-side, not 422'd server-side. */
export const MESSAGE_TRANSLATE_TARGET_LOCALES_LIMIT = 50

/** Mirrors `messageRequestTranslationBatchSchema`'s combined items x locales cap (`MAX_BATCH_TRANSLATION_EVENTS` in `message.schema.ts`) — bounds total translation-requested events per batch call, not just each dimension independently. */
export const MESSAGE_TRANSLATE_BATCH_COMBINED_LIMIT = 100

/** Mirrors `messageRequestTranslationBatchSchema`'s `items.max(20)` — how many distinct source messages one batch call may target. */
export const MESSAGE_TRANSLATE_BATCH_ITEMS_LIMIT = 20

export const MESSAGE_TYPE_LABEL: Record<MessageType, string> = {
	[MessageType.TAGLINE]: 'Tagline',
	[MessageType.COPY_IN]: 'Copy In',
	[MessageType.COPY_OUT]: 'Copy Out',
	[MessageType.CTA]: 'CTA',
	[MessageType.STAGE]: 'Stage',
	[MessageType.TITLE]: 'Title',
	[MessageType.BODY]: 'Body',
	[MessageType.MAIN_CLAIM]: 'Main Claim',
	[MessageType.SECONDARY_CLAIM]: 'Secondary Claim',
	[MessageType.COPYRIGHT]: 'Copyright',
	[MessageType.DISCLAIMER]: 'Disclaimer',
	[MessageType.VEEVA_CODE]: 'Veeva Code',
	[MessageType.AI_DISCLAIMER]: 'AI Disclaimer',
}

export const MESSAGE_STATUS_LABEL: Record<MessageStatus, string> = {
	[MessageStatus.DRAFT]: 'Draft',
	[MessageStatus.ACTIVE]: 'Active',
	[MessageStatus.ARCHIVED]: 'Archived',
}
